// EAA-1667: Permission context/provider, moved into packages/hooks so every app
// reads the same `usePermissions()` gate. The app builds a PermissionState (via
// createPermissionState fed from its own store/API) and mounts PermissionProvider;
// consumers read it. Fail-closed by default — like the auth capability seam, a
// missing provider denies everything rather than silently allowing it.
import React, { createContext, useContext } from 'react';

import type { PermissionState } from './createPermissionState';

const failClosedPermissionState: PermissionState = {
    hasUser: false,
    effectivePermissions: [],
    permissionSet: new Set<string>(),
    permissionsLoading: false,
    permissionsLoaded: false,
    permissionsError: null,
    mergedScreenRules: [],
    screenRulesLoaded: false,
    screenRulesError: null,
    hasCatalogPermission: () => false,
    hasScreenAccess: () => false,
    canManageTenantPermissions: false,
    // Fail-closed means "denied", not "we could not tell" — a missing provider
    // is a wiring bug, and offering a retry for it would be a lie.
    permissionsUnavailable: false,
    actingTenantMissing: false,
    retryPermissions: () => undefined,
};

const PermissionContext = createContext<PermissionState>(failClosedPermissionState);
PermissionContext.displayName = 'PermissionContext';

export interface PermissionProviderProps {
    value: PermissionState;
    children: React.ReactNode;
}

/**
 * Mounted once per app shell (inside the store provider), wiring the app's
 * resolved {@link PermissionState} into the `usePermissions()` contract.
 */
export const PermissionProvider: React.FC<PermissionProviderProps> = ({ value, children }) => (
    <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>
);

/** Read the current permission gate + status. */
export function usePermissions(): PermissionState {
    return useContext(PermissionContext);
}
